import api from './api'

/**
 * Retail CRM API client. Wraps all `/v1/admin/crm/*` endpoints.
 *
 * Mirrors `eventsApi.js`: the shape follows the backend route file so a UI call
 * traces straight back to it, and **every method returns the whole envelope**
 * rather than unwrapping `data`.
 *
 * THAT IS NOT A STYLE CHOICE. Several CRM endpoints carry their payload as
 * top-level siblings of `data` rather than inside it, and two carry no `data`
 * key at all:
 *
 *   GET  /crm/:id                    → data, allowedTransitions, scoreExplanation,
 *                                      distanceAssessment, permit, nextAction,
 *                                      contacts, activities, outreach, transitions
 *   GET  /crm/attention              → NO `data`; needsYou, agentsAreHandling, headline
 *   POST /crm/:id/transition/propose → NO `data`; legal, requiresApproval, allowedTransitions
 *   POST /crm/:id/transition         → data, transition, allowedTransitions
 *
 * A client that unwrapped `data` would silently return `undefined` for the
 * attention lane and the propose dry-run, and would throw away the very field
 * the detail page renders its buttons from.
 *
 * ALLOWED TRANSITIONS ALWAYS COME FROM THE SERVER. The state machine has an
 * approval gate, an agent-actor gate and a permit entry guard; none of them are
 * knowable from a stage name. `constants/crmStages.js` styles what the server
 * says is possible and never computes its own list.
 */

// ─── Accounts ────────────────────────────────────────────────────────────
const listAccounts = async (params = {}, options = {}) => {
  const { data } = await api.get('/crm/accounts', { ...options, params })
  return data
}
const getAccount = async (id, params = {}) => {
  const { data } = await api.get(`/crm/${id}`, { params })
  return data
}
const createAccount = async (payload) => {
  const { data } = await api.post('/crm/accounts', payload)
  return data
}
const updateAccount = async (id, payload) => {
  const { data } = await api.put(`/crm/${id}`, payload)
  return data
}
const getStats = async (params = {}) => {
  const { data } = await api.get('/crm/accounts/stats', { params })
  return data
}
const getAttention = async (params = {}) => {
  const { data } = await api.get('/crm/attention', { params })
  return data
}

/**
 * Move an account to a new stage.
 *
 * The one approval-gated move (PERMIT_VERIFIED → FIRST_ORDER) requires an
 * `approval` object AND a non-agent actor. Both refusals arrive as 403 with
 * `allowedTransitions` attached, so the caller can re-render honest buttons
 * rather than guessing.
 */
const transitionAccount = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/transition`, payload)
  return data
}
/** Dry run — what WOULD happen. Returns no `data` key; read `legal` / `requiresApproval`. */
const proposeTransition = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/transition/propose`, payload)
  return data
}
const scoreAccount = async (id) => {
  const { data } = await api.post(`/crm/${id}/score`)
  return data
}
/** A BILLED routing call. Only ever from an explicit button, never on page load. */
const routeAccount = async (id, payload = {}) => {
  const { data } = await api.post(`/crm/${id}/route`, payload)
  return data
}

// ─── Activities ──────────────────────────────────────────────────────────
const listActivities = async (id, params = {}) => {
  const { data } = await api.get(`/crm/${id}/activities`, { params })
  return data
}
/** Returns the created activity plus `suggestedTransition` — a suggestion, never a commit. */
const logActivity = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/activities`, payload)
  return data
}

// ─── Permit ──────────────────────────────────────────────────────────────
/** Human-only (crm.approve). An API key can never hold that scope. */
const verifyPermit = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/permit/verify`, payload)
  return data
}
/** Downgrade only — anyone with manage may raise a doubt, nobody may clear one. */
const flagPermit = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/permit/flag`, payload)
  return data
}

// ─── Contacts ────────────────────────────────────────────────────────────
const listContacts = async (id, params = {}) => {
  const { data } = await api.get(`/crm/${id}/contacts`, { params })
  return data
}
const createContact = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/contacts`, payload)
  return data
}
const updateContact = async (contactId, payload) => {
  const { data } = await api.put(`/crm/contacts/${contactId}`, payload)
  return data
}
const optOutContact = async (contactId, payload = {}) => {
  const { data } = await api.post(`/crm/contacts/${contactId}/opt-out`, payload)
  return data
}
/** Reversing an opt-out is human-only: a machine must never un-say someone's "stop". */
const optInContact = async (contactId, payload = {}) => {
  const { data } = await api.post(`/crm/contacts/${contactId}/opt-in`, payload)
  return data
}

// ─── Outreach ────────────────────────────────────────────────────────────
const listOutreach = async (id, params = {}) => {
  const { data } = await api.get(`/crm/${id}/outreach`, { params })
  return data
}
const draftOutreach = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/outreach/draft`, payload)
  return data
}
const updateOutreach = async (id, messageId, payload) => {
  const { data } = await api.put(`/crm/${id}/outreach/${messageId}`, payload)
  return data
}
const approveOutreach = async (id, messageId, payload = {}) => {
  const { data } = await api.post(`/crm/${id}/outreach/${messageId}/approve`, payload)
  return data
}
const sendOutreach = async (id, messageId, payload = {}) => {
  const { data } = await api.post(`/crm/${id}/outreach/${messageId}/send`, payload)
  return data
}

// ─── Orders (the prospect → customer seam; human-only) ───────────────────
const firstOrder = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/first-order`, payload)
  return data
}
const reorder = async (id, payload) => {
  const { data } = await api.post(`/crm/${id}/reorder`, payload)
  return data
}

// ─── Territories & settings ──────────────────────────────────────────────
const listTerritories = async () => {
  const { data } = await api.get('/crm/territories')
  return data
}
const updateTerritory = async (territoryId, payload) => {
  const { data } = await api.put(`/crm/territories/${territoryId}`, payload)
  return data
}
const getSettings = async () => {
  const { data } = await api.get('/crm/settings')
  return data
}
/** `reason` is MANDATORY — a moved target nobody can defend later is not a target. */
const updateSettings = async (payload) => {
  const { data } = await api.put('/crm/settings', payload)
  return data
}

// ─── Planner (ships with the planner slice) ──────────────────────────────
const getWeekPlan = async (params = {}) => {
  const { data } = await api.get('/crm/plan/week', { params })
  return data
}
const listReps = async (options = {}) => {
  const { data } = await api.get('/crm/plan/reps', options)
  return data
}
const saveRouteDraft = async (payload) => {
  const { data } = await api.post('/crm/plan/drafts', payload)
  return data
}
const approveRouteDraft = async (id, inputsHash) => {
  const { data } = await api.post(`/crm/plan/drafts/${id}/approve`, { inputsHash })
  return data
}
const discardRouteDraft = async (id, inputsHash, reason) => {
  const { data } = await api.post(`/crm/plan/drafts/${id}/discard`, { inputsHash, reason })
  return data
}
/**
 * Preview persists NOTHING, which is why it is gated on `crm.view` rather than
 * `crm.manage` — proposing a week is not committing to one.
 */
const previewPlan = async (payload) => {
  const { data } = await api.post('/crm/plan/week/preview', payload)
  return data
}
const generatePlan = async (payload) => {
  const { data } = await api.post('/crm/plan/week/generate', payload)
  return data
}
const regeneratePlan = async (payload) => {
  const { data } = await api.post('/crm/plan/week/regenerate', payload)
  return data
}
const updateRouteDay = async (dayId, payload) => {
  const { data } = await api.put(`/crm/plan/days/${dayId}`, payload)
  return data
}
const startRouteDay = async (dayId, payload = {}) => {
  const { data } = await api.post(`/crm/plan/days/${dayId}/start`, payload)
  return data
}
const completeRouteDay = async (dayId, payload = {}) => {
  const { data } = await api.post(`/crm/plan/days/${dayId}/complete`, payload)
  return data
}
/** Creates the activity, marks the stop and applies cadence. Does NOT move the stage. */
const markStopVisited = async (dayId, stopId, payload) => {
  const { data } = await api.post(`/crm/plan/days/${dayId}/stops/${stopId}/visited`, payload)
  return data
}
const skipStop = async (dayId, stopId, payload) => {
  const { data } = await api.post(`/crm/plan/days/${dayId}/stops/${stopId}/skip`, payload)
  return data
}
const getToday = async (params = {}) => {
  const { data } = await api.get('/crm/today', { params })
  return data
}
/** Feeds the Log Activity dialog's default follow-up date, with the rule that produced it. */
const nextAction = async (params = {}) => {
  const { data } = await api.get('/crm/cadence/next-action', { params })
  return data
}

// ─── Imports (ships with the import slice) ───────────────────────────────
/**
 * Upload and PLAN only — nothing is written to retail_accounts. A hand-built
 * spreadsheet of 600 leads is not a data feed, and a single-shot import either
 * forks accounts or drops them silently.
 */
const uploadImport = async (formData, config = {}) => {
  const { data } = await api.post('/crm/imports', formData, config)
  return data
}
/** Unpaginated by design — there are tens of these, not thousands. */
const listImports = async () => {
  const { data } = await api.get('/crm/imports')
  return data
}
const getImport = async (batchId) => {
  const { data } = await api.get(`/crm/imports/${batchId}`)
  return data
}
const resolveImportRow = async (batchId, rowId, payload) => {
  const { data } = await api.put(`/crm/imports/${batchId}/rows/${rowId}`, payload)
  return data
}
const commitImport = async (batchId, params = {}) => {
  const { data } = await api.post(`/crm/imports/${batchId}/commit`, {}, { params })
  return data
}
const discardImport = async (batchId) => {
  const { data } = await api.post(`/crm/imports/${batchId}/discard`)
  return data
}

/**
 * Rewrite the API's remedies into the vocabulary of the screen showing them.
 *
 * The backend's notes and remedies are written for whoever calls the endpoint,
 * and that is usually right: MCP and the digest both consume them, and
 * "POST /crm/plan/week/preview" is exactly the useful instruction there. On a
 * screen it is a dead end — the two people who use the route calendar are field
 * reps, the button they need is eight inches away and says "Preview plan", and
 * telling them to make an HTTP request is the kind of remedy CLAUDE.md bans.
 *
 * Only endpoints with a button on some screen are rewritten. Anything unmapped
 * is passed through untouched: a technical sentence is worse than a plain one
 * and far better than a silently truncated one.
 */
const SCREEN_ACTIONS = [
  [/POST\s+\/crm\/plan\/week\/preview/gi, 'the Suggest a route button'],
  [/POST\s+\/crm\/plan\/week\/(generate|regenerate)/gi, 'the route review in the calendar'],
  [/GET\s+\/crm\/plan\/week(\?[^\s.,)]*)?/gi, 'the route calendar'],
  [/POST\s+\/crm\/[^\s/]+\/route/gi, 'the Measure drive time button'],
  [/GET\s+\/crm\/accounts[^\s.,)]*/gi, 'the accounts list'],
  [/GET\s+\/crm\/imports[^\s.,)]*/gi, 'the imports page'],
  // Column names leak into the planner's notes the same way endpoints do.
  // "accounts carry an owner_user_id" is precise and unreadable to the two reps
  // this screen exists for.
  [/\bowner_user_id\b/g, 'assigned owner'],
  [/\bretail_accounts\b/g, 'the accounts list'],
  [/\bcrm_settings\b/g, 'the CRM settings']
]

export function humaniseNote (note) {
  if (!note) return note
  let out = String(note)
  for (const [pattern, phrase] of SCREEN_ACTIONS) out = out.replace(pattern, phrase)
  // "Check the week with the route calendar" is the one phrasing the swap
  // leaves ungrammatical; every other mapping reads correctly in place.
  return out
    .replace(/\bwith the route calendar\b/gi, 'on the route calendar')
    .replace(/\bwith the accounts list\b/gi, 'in the accounts list')
}

/**
 * Turn any thrown error into the `{ error, remedy, code }` the UI shows inline.
 *
 * Three cases the CRM actually produces, and each needs different words:
 *
 *   501 NOT_IMPLEMENTED — the endpoint exists but its slice is not deployed yet.
 *       The backend says so explicitly ("Nothing is wrong with your request"),
 *       and a screen that rendered that as a red failure would send someone
 *       hunting a bug that does not exist. `pending` marks it so views can show
 *       an informational empty state instead.
 *   4xx with a refusal envelope — `{ code, error, remedy }`. Show the remedy;
 *       it is the whole point of the convention.
 *   A transport error — no response at all. Say so plainly.
 */
export function describeError (e) {
  const status = e?.response?.status ?? null
  const body = e?.response?.data ?? null

  if (status === 501) {
    return {
      status,
      pending: true,
      code: body?.code || 'NOT_IMPLEMENTED',
      error: body?.error || 'This part of the CRM is not deployed yet.',
      remedy: humaniseNote(body?.remedy) || 'It ships with a later slice. Nothing is wrong with your request.'
    }
  }

  return {
    status,
    pending: false,
    code: body?.code || null,
    error: body?.error || e?.message || 'Something went wrong.',
    remedy: humaniseNote(body?.remedy) || null
  }
}

export default {
  listReps,
  saveRouteDraft,
  approveRouteDraft,
  discardRouteDraft,
  listAccounts,
  getAccount,
  createAccount,
  updateAccount,
  getStats,
  getAttention,
  transitionAccount,
  proposeTransition,
  scoreAccount,
  routeAccount,
  listActivities,
  logActivity,
  verifyPermit,
  flagPermit,
  listContacts,
  createContact,
  updateContact,
  optOutContact,
  optInContact,
  listOutreach,
  draftOutreach,
  updateOutreach,
  approveOutreach,
  sendOutreach,
  firstOrder,
  reorder,
  listTerritories,
  updateTerritory,
  getSettings,
  updateSettings,
  getWeekPlan,
  previewPlan,
  generatePlan,
  regeneratePlan,
  updateRouteDay,
  startRouteDay,
  completeRouteDay,
  markStopVisited,
  skipStop,
  getToday,
  nextAction,
  uploadImport,
  listImports,
  getImport,
  resolveImportRow,
  commitImport,
  discardImport,
  describeError,
  humaniseNote
}
